import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, Loader2, Mail, Phone, UserPlus, Building2, CheckCircle } from 'lucide-react';
import { searchProfessionals, inviteProfessional, type ProfessionalBusiness } from '@/api/professionals';

export interface AssignedProfessional {
  id: string;
  name: string;
  type: string;
  source: 'existing' | 'invited';
  email?: string | null;
  phone?: string | null;
}

interface AssignProfessionalModalProps {
  isOpen: boolean;
  onClose: () => void;
  professionalType: 'veterinary' | 'grooming' | 'training' | 'other';
  professionalLabel: string;
  petId: string;
  onAssign: (professional: AssignedProfessional) => void;
}

const TYPE_SEARCH_MAP: Record<string, string> = {
  veterinary: 'veterinary',
  grooming: 'grooming',
  training: 'training',
  other: '',
};

export function AssignProfessionalModal({
  isOpen,
  onClose,
  professionalType,
  professionalLabel,
  petId,
  onAssign,
}: AssignProfessionalModalProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [results, setResults] = useState<ProfessionalBusiness[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const searchRef = useRef<HTMLInputElement>(null);

  // Focus search input on open
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setBusinessName('');
      setEmail('');
      setPhone('');
      setResults([]);
      setHasSearched(false);
      setSuccess(false);
      setError('');
    }
  }, [isOpen]);

  const doSearch = useCallback(
    async (q: string) => {
      if (!q.trim() || q.trim().length < 2) {
        setResults([]);
        setHasSearched(false);
        return;
      }
      setIsSearching(true);
      try {
        const res = await searchProfessionals(q, TYPE_SEARCH_MAP[professionalType]);
        setResults(res.businesses);
        setHasSearched(true);
      } catch {
        setResults([]);
        setHasSearched(true);
      } finally {
        setIsSearching(false);
      }
    },
    [professionalType],
  );

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => doSearch(value), 400);
  };

  const handleSelectBusiness = (biz: ProfessionalBusiness) => {
    onAssign({
      id: biz.id,
      name: biz.name,
      type: professionalType,
      source: 'existing',
      email: biz.email,
      phone: biz.phone,
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (!businessName.trim() && !email.trim() && !phone.trim()) return;
    setIsSubmitting(true);
    setError('');
    try {
      const res = await inviteProfessional({
        name: businessName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        type: professionalType,
        petId,
      });
      onAssign({
        id: res.invitationId,
        name: businessName.trim() || email.trim() || phone.trim() || 'Professional',
        type: professionalType,
        source: 'invited',
        email: email.trim() || null,
        phone: phone.trim() || null,
      });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(t('assignProfessional.somethingWentWrong'));
      console.error('Failed to invite professional:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop — only closes on direct click, not bubbled clicks */}
      <div
        className="absolute inset-0 bg-black/50"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#1A1929] rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('assignProfessional.assign', { label: professionalLabel })}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {success ? (
            <div className="text-center py-8">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                {email ? t('assignProfessional.invitationSent') : t('assignProfessional.professionalAdded')}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {email
                  ? t('assignProfessional.emailSentTo', { email })
                  : t('assignProfessional.hasBeenAdded', { name: businessName })}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search existing professionals */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('assignProfessional.searchExistingProfessionals')}
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder={t('assignProfessional.searchPlaceholder', { label: professionalLabel.toLowerCase() })}
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-purple-500" />
                  )}
                </div>
              </div>

              {/* Search Results */}
              {results.length > 0 && (
                <div className="border border-purple-200 dark:border-purple-800 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-200 dark:border-purple-800">
                    <p className="text-xs font-medium text-purple-700 dark:text-purple-300">
                      {t('assignProfessional.foundOnPetsoPro')}
                    </p>
                  </div>
                  <div className="max-h-40 overflow-y-auto">
                    {results.map((biz) => (
                      <button
                        key={biz.id}
                        onClick={() => handleSelectBusiness(biz)}
                        className="w-full text-left px-3 py-2.5 hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                      >
                        <div className="flex items-center gap-3">
                          {biz.logoUrl ? (
                            <img src={biz.logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                              <Building2 className="w-4 h-4 text-purple-600" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{biz.name}</p>
                            <div className="flex items-center gap-3 mt-0.5">
                              {biz.email && (
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <Mail className="w-3 h-3" />{biz.email}
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-purple-600 font-medium">{t('assignProfessional.select')}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasSearched && results.length === 0 && searchQuery.trim().length >= 2 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2">
                  {t('assignProfessional.notFoundOnPetsoPro')}
                </p>
              )}

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
                <span className="text-xs text-gray-400 dark:text-gray-500 uppercase">{t('assignProfessional.orAddManually')}</span>
                <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Building2 className="w-3.5 h-3.5" />
                    {t('assignProfessional.businessPracticeName')}
                  </span>
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={`e.g., Happy Paws ${professionalLabel === 'Veterinarian' ? 'Veterinary Clinic' : professionalLabel === 'Groomer' ? 'Grooming Salon' : 'Academy'}`}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {t('assignProfessional.email')}
                  </span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="e.g., info@happypaws.com"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                {email.trim() && (
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                    {t('assignProfessional.invitationEmailWillBeSent')}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {t('assignProfessional.contactNumber')}
                  </span>
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="e.g., +44 7700 900000"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-xs text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Info box */}
              <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                  {email.trim()
                    ? t('assignProfessional.emailInvitationInfo', { name: businessName || 'The professional' })
                    : t('assignProfessional.noEmailInfo')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="p-5 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSubmit}
              disabled={(!businessName.trim() && !email.trim() && !phone.trim()) || isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4" />
              )}
              {email.trim() ? t('assignProfessional.assignAndSendInvitation') : t('assignProfessional.assign', { label: professionalLabel })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
